/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */
import {ActionTree, Commit} from 'vuex';
import {JsonLdCollection, RootState} from '@/store/types';
import {AxiosResponse} from 'axios';
import {api} from '@/services/http';
import {
    AudioApiCallInterface, DlcInterface,
    FavouritesInterface,
    GameApiCallInterface,
    GameStateInterface,
    SpeciesInterface,
} from '@/store/modules/game/types';
import {Vue} from 'vue-property-decorator';

/**
 * Actions for game operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<GameStateInterface, RootState> = {
    /**
     * Call game API.
     * @param {Commit} commit
     * @param {GameApiCallInterface} config
     */
    callGameApi({commit}: { commit: Commit }, config: GameApiCallInterface): void {
        let url: string = `game://${config.base || 'trade-centre'}/${config.route}`;
        const params: { [key: string]: string | number } = config.params || {};
        url += '?' + Object.keys(params).map((key: string) => `${key}=${params[key]}`).join('&');
        document.location.href = url;
    },

    /**
     * Call game API.
     * @param {Commit} commit
     * @param {AudioApiCallInterface} config
     */
    callAudioApi({commit}: { commit: Commit }, config: AudioApiCallInterface): void {
        try {
            let url: string = `audio://${config.type}`;
            const params: { [key: string]: string | number } = config.params || {};
            url += '?' + Object.keys(params).map((key: string) => `${key}=${params[key]}`).join('&');
            document.location.href = url.replace(/[?]$/, '');
        } catch (e) {
            // do nothing
        }
    },

    /**
     * Set the app to be locked.
     * @param {Commit} commit
     * @param {boolean} locked
     */
    setLocked({commit}: { commit: Commit }, locked: boolean): void {
        commit('setLocked', locked);
    },

    /**
     * Set home zoo.
     * @param {Commit} commit
     * @param {string} zoo
     */
    setZoo({commit}: { commit: Commit }, zoo: string): void {
        commit('setZoo', zoo);
    },

    /**
     * Set cash balance.
     * @param {Commit} commit
     * @param {number} cash
     */
    setCash({commit}: { commit: Commit }, cash: number): void {
        commit('setCash', cash);
    },

    /**
     * Load dlc for user.
     * @param {Commit} commit
     * @return {Promise<JsonLdCollection<DlcInterface>>}
     */
    async getDlc({commit}: { commit: Commit }): Promise<JsonLdCollection<DlcInterface>> {
        return api.get('api/zoo/dlcs', {cache: true})
            .then((response: AxiosResponse<JsonLdCollection<DlcInterface>>) => {
                const locale: string = (Vue.i18n.locale() || 'en').replace(/[-][^-]+$/, '');
                commit('setDlc', (response.data['hydra:member'] || [])
                    .sort((a: DlcInterface, b: DlcInterface): number =>
                        a.description.localeCompare(b.description, locale)));
                return response.data;
            });
    },

    /**
     * Load species.
     * @param {Commit} commit
     * @return {Promise<JsonLdCollection<SpeciesInterface>>}
     */
    async getSpecies({commit}: { commit: Commit }): Promise<JsonLdCollection<SpeciesInterface>> {
        return api.get('api/zoo/species', {cache: true})
            .then((response: AxiosResponse<JsonLdCollection<SpeciesInterface>>) => {
                const locale: string = (Vue.i18n.locale() || 'en').replace(/[-][^-]+$/, '');
                commit('setSpecies', (response.data['hydra:member'] || [])
                    .sort((a: SpeciesInterface, b: SpeciesInterface): number =>
                        a.commonName.localeCompare(b.commonName, locale)));
                return response.data;
            });
    },

    /**
     * Load favourites.
     * @param {Commit} commit
     * @return {Promise<JsonLdCollection<FavouritesInterface>>}
     */
    async getFavourites({commit}: { commit: Commit }): Promise<JsonLdCollection<FavouritesInterface>> {
        return api.get('api/zoo/favourites', {cache: true})
            .then((response: AxiosResponse<JsonLdCollection<FavouritesInterface>>) => {
                commit('setFavourites', response.data['hydra:member']);
                return response.data;
            });
    },

    /**
     * Adds animal to favourites.
     * @param {Commit} commit
     * @param {string} animal
     * @return {Promise<FavouritesInterface>}
     */
    async addFavourite({commit}: { commit: Commit }, animal: string): Promise<FavouritesInterface> {
        return api.post('/api/zoo/favourites', {animal})
            .then((response: AxiosResponse<FavouritesInterface>) => {
                commit('addFavourite', response.data);
                return response.data;
            });
    },

    /**
     * Adds animal to favourites.
     * @param {Commit} commit
     * @param {FavouritesInterface} favourite
     * @return {Promise<any>}
     */
    async removeFavourite({commit}: { commit: Commit }, favourite: FavouritesInterface): Promise<any> {
        return api.delete(favourite['@id'])
            .then((response: AxiosResponse<FavouritesInterface>) => {
                commit('removeFavourite', favourite);
                return response.data;
            });
    },
};
